import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { VideoSDKAdapter } from '../core/adapter';
import { currentOutputDeviceIdAtom, videoSDKAdapterAtom } from '../core/atoms';
import { useStoredAudioVideoSettings } from '../core/hooks/useStoredAudioVideoSettings';
import { localAudioContextAtom } from '../core/room/participant/media/atoms';

export function useAudioOutput() {
  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);
  const audioOutputDeviceId = useAtomValue(currentOutputDeviceIdAtom);
  const getAudioOutputDeviceId = useAtomCallbackMemoized((get) => get(currentOutputDeviceIdAtom), []);

  const switchAudioOutputDevice = useCallback<VideoSDKAdapter['switchAudioOutput']>(
    async (options) => {
      return await getAdapter().switchAudioOutput(options);
    },
    [getAdapter],
  );

  return {
    audioOutputDeviceId,
    getAudioOutputDeviceId,
    switchAudioOutputDevice,
  };
}

export function useMuteAudio() {
  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);
  const setLocalAudioContext = useSetAtom(localAudioContextAtom);
  const getLocalAudioContext = useAtomCallbackMemoized((get) => get(localAudioContextAtom), []);
  return useCallback(
    async (shouldReleaseTrack?: boolean): Promise<boolean> => {
      return await getAdapter().muteAudio(getLocalAudioContext, setLocalAudioContext, shouldReleaseTrack);
    },
    [getAdapter, setLocalAudioContext, getLocalAudioContext],
  );
}

export function useUnmuteAudio() {
  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);
  const getLocalAudioContext = useAtomCallbackMemoized((get) => get(localAudioContextAtom), []);
  const setLocalAudioContext = useSetAtom(localAudioContextAtom);
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();
  return useCallback(async (): Promise<boolean> => {
    return await getAdapter().unmuteAudio(getLocalAudioContext, setLocalAudioContext, setStoredAudioVideoSettings);
  }, [getAdapter, setLocalAudioContext, getLocalAudioContext, setStoredAudioVideoSettings]);
}

export function useSwitchMicrophone() {
  const getAdapter = useAtomCallbackMemoized((get) => get(videoSDKAdapterAtom), []);
  const getLocalAudioContext = useAtomCallbackMemoized((get) => get(localAudioContextAtom), []);
  const setLocalAudioContext = useSetAtom(localAudioContextAtom);
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();

  return useCallback(
    async (microphoneId: string): Promise<boolean> => {
      return await getAdapter().switchMicrophone(
        microphoneId,
        getLocalAudioContext,
        setLocalAudioContext,
        setStoredAudioVideoSettings,
      );
    },
    [getAdapter, setLocalAudioContext, getLocalAudioContext, setStoredAudioVideoSettings],
  );
}
